import { Injectable, Injector } from '@angular/core';
import { environment } from '@env/environment';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  NzNotificationDataOptions,
  NzNotificationService
} from 'ng-zorro-antd/notification';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { normalizeSignalRBaseUrl } from '../utils/signalr-url';
import { SupportAccessService } from './support-access.service';

// ============================================================================
// Types & Interfaces
// ============================================================================

type DisplayType = 'modal' | 'toast' | 'msg' | 'none';

export interface ReceiveMessageNotify {
  id: string;
  createdAt: number;
  title: string;
  description: string;
  type: string;
  displayType: DisplayType;
  link: string;
  json: string;
  data: NzSafeAny;
}

export interface AINotificationMessage {
  createdAt: number;
  title: string;
  description: string;
  type: string;
  displayType: DisplayType;
  data: NzSafeAny;
}

export interface PromptCardStatusUpdate {
  promptCardId: number;
  data?: NzSafeAny;
  status?: string;
  [key: string]: unknown;
}

export interface OcrScanProcessMessage {
  ocrType: string;
  jobId: string;
  status: number;
  ocrEntryId: string;
  fileName: string;
  totalPages: number;
  processedPages: number;
  progressPercent: number;
  errorMessage: string | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getAccessToken(): string {
  const storedValue = localStorage.getItem('qca_token');
  const tokenData = JSON.parse(storedValue || '{}') || {};
  return tokenData.token ?? '';
}

function isAnonymousPage(): boolean {
  return window.location.href.includes('/full/');
}

// ============================================================================
// Notification Service
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  readonly uuid = uuid();

  // ---------------------------------------------------------------------------
  // Connection State
  // ---------------------------------------------------------------------------
  private hubConnection: HubConnection | null = null;
  private reconnectInterval: ReturnType<typeof setInterval> | null = null;
  private tenantId = '';
  private userId = '';

  // ---------------------------------------------------------------------------
  // Notification Subjects
  // ---------------------------------------------------------------------------
  private readonly generalMessageSubject = new Subject<ReceiveMessageNotify>();
  private readonly aiNotificationSubject = new Subject<AINotificationMessage>();
  private readonly bankNotificationSubject = new Subject<string>();
  private readonly financialAdviceSubject = new Subject<string>();
  private readonly einvSubmissionSubject = new Subject<string>();
  private readonly emailProgressSubject = new BehaviorSubject<number>(0);
  private readonly promptCardUpdatesSubject = new Subject<PromptCardStatusUpdate>();
  private readonly latestPromptCardByPromptId = new Map<number, PromptCardStatusUpdate>();
  private readonly ocrScanProcessSubject = new Subject<OcrScanProcessMessage>();

  // ---------------------------------------------------------------------------
  // Public Observables
  // ---------------------------------------------------------------------------
  public notification$: Observable<ReceiveMessageNotify> | undefined;
  public aiNotification$: Observable<AINotificationMessage> | undefined;
  public readonly emailProgress$ = this.emailProgressSubject.asObservable();
  public readonly ocrScanProcess$ = this.ocrScanProcessSubject.asObservable();

  constructor(
    private nzNotification: NzNotificationService,
    private nzModal: NzModalService,
    private nzMessage: NzMessageService,
    private injector: Injector
  ) {
    console.log(`NotificationService initialized [uuid: ${this.uuid}]`);
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  init(tid: string, uid: string): void {
    this.stopConnection();
    this.tenantId = tid;
    this.userId = uid;
    this.notification$ = this.generalMessageSubject.asObservable();
    this.aiNotification$ = this.aiNotificationSubject.asObservable();
    this.connectHub();
  }

  // ===========================================================================
  // Public API - Observable Accessors
  // ===========================================================================

  getPromptCardUpdates$(): Observable<PromptCardStatusUpdate> {
    return this.promptCardUpdatesSubject.asObservable();
  }

  getLatestPromptCardUpdate(promptCardId: number): PromptCardStatusUpdate | undefined {
    return this.latestPromptCardByPromptId.get(promptCardId);
  }

  // ===========================================================================
  // Public API - Actions
  // ===========================================================================

  resetEmailProgress(): void {
    this.emailProgressSubject.next(0);
  }

  registerTerminal(terminalId: string, terminalName: string): void {
    if (!this.isConnected()) return;

    this.hubConnection!
      .invoke('RegisterTerminal', terminalId, terminalName)
      .then(() => console.log(`Registered terminal: ${terminalId}-${terminalName}`))
      .catch(err => console.error(`Error registering terminal: ${err}`));
  }

  // ===========================================================================
  // Hub Connection Management
  // ===========================================================================

  private isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  private getHubUrl(): string {
    const url =
      (environment.api as { signalrUrl?: string }).signalrUrl ||
      environment.api.baseUrl;
    return normalizeSignalRBaseUrl(url);
  }

  private createConnectionBuilder(serverUrl: string): HubConnectionBuilder {
    const baseUrl = normalizeSignalRBaseUrl(serverUrl);
    return new HubConnectionBuilder().withUrl(`${baseUrl}notificationhub`, {
      accessTokenFactory: () => getAccessToken()
    });
  }

  private connectHub(): void {
    if (!this.tenantId) return;

    const hubBaseUrl = this.getHubUrl();
    const hubUrl = `${hubBaseUrl}notificationhub`;
    console.log(`SignalR connecting to ${hubUrl} (tid=${this.tenantId}, uid=${this.userId})`);

    this.hubConnection = this.createConnectionBuilder(hubBaseUrl).build();
    this.registerHubEventHandlers();
    this.startConnection(hubUrl);
  }

  private registerHubEventHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('ReceiveMessage', (message: ReceiveMessageNotify) => {
      console.log('SignalR received ReceiveMessage', message);
      this.handleGeneralMessage(message);
    });

    this.hubConnection.on('DebitCreditNotification', (message: string) => {
      console.log('SignalR received DebitCreditNotification', message);
      this.bankNotificationSubject.next(message);
    });

    this.hubConnection.on('FinancialAdviceNotification', (message: string) => {
      this.financialAdviceSubject.next(message);
    });

    this.hubConnection.on('EInvSubmissionNotification', (message: string) => {
      this.einvSubmissionSubject.next(message);
    });

    this.hubConnection.on('ReceiveProgress', (progress: number) => {
      this.emailProgressSubject.next(progress);
    });

    this.hubConnection.on('AINotification', (message: AINotificationMessage) => {
      console.log('SignalR received AINotification', message);
      this.handleAIMessage(message);
    });

    this.hubConnection.onclose(() => this.handleConnectionClosed());
  }

  private startConnection(hubUrl: string): void {
    this.hubConnection!
      .start()
      .then(() => {
        this.clearReconnectInterval();
        this.joinGroups();
        console.log(`SignalR hub connected to ${hubUrl}`);
      })
      .catch(err => console.error(`Error establishing SignalR connection: ${err}`));
  }

  private joinGroups(): void {
    if (!this.tenantId || !this.userId || !this.hubConnection) return;

    const tid = this.tenantId.toLowerCase();
    const uid = this.userId.toLowerCase();
    const tidUidGroup = `${tid}_${uid}`;

    Promise.all([
      this.hubConnection.invoke('JoinGroup', tid),
      this.hubConnection.invoke('JoinGroup', tidUidGroup),
      this.hubConnection.invoke('JoinGroup', uid)
    ])
      .then(() => console.log(`Joined groups: ${tid}, ${tidUidGroup}, ${uid}`))
      .catch(err => console.error(`Error joining groups: ${err}`));
  }

  private handleConnectionClosed(): void {
    console.log('SignalR connection closed, attempting reconnect...');
    this.clearReconnectInterval();

    if (this.tenantId) {
      this.reconnectInterval = setInterval(() => this.connectHub(), 5000);
    }
  }

  private stopConnection(): void {
    if (!this.hubConnection) return;

    this.tenantId = '';
    this.hubConnection
      .stop()
      .then(() => console.log('SignalR connection stopped'))
      .catch(err => console.error(`Error stopping SignalR connection: ${err}`));

    this.hubConnection = null;
    this.clearReconnectInterval();
  }

  private clearReconnectInterval(): void {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  // ===========================================================================
  // Message Handling
  // ===========================================================================

  private handleGeneralMessage(message: ReceiveMessageNotify): void {
    if (isAnonymousPage()) return;

    if (message.type === 'report-task') {
      this.showReportTaskNotification(message);
    } else if (message.type === 'support-access') {
      const supportAccessSrv = this.injector.get(SupportAccessService);
      supportAccessSrv.restrictSupportAccess();
      console.log('Received support access update message');
    } else {
      this.displayNotification(message.displayType, message.title, message.description);
    }

    this.generalMessageSubject.next(message);
  }

  private handleAIMessage(message: AINotificationMessage): void {
    if (isAnonymousPage()) return;

    console.log('Received AI message:', message);

    if (message.type === 'ocr-scan-process' && message.data) {
      console.log('SignalR received ocr-scan-process', message.data);
      this.ocrScanProcessSubject.next(message.data as OcrScanProcessMessage);
      return;
    }

    this.displayNotification(message.displayType, message.title, message.description);
    this.aiNotificationSubject.next(message);
  }

  private displayNotification(displayType: DisplayType, title: string, description: string): void {
    switch (displayType) {
      case 'modal':
        this.nzModal.create({
          nzTitle: title,
          nzContent: description,
          nzFooter: null
        });
        break;
      case 'toast':
        this.nzNotification.info(title, description);
        break;
      case 'msg':
        this.nzMessage.info(description);
        break;
      case 'none':
        break;
      default:
        this.nzNotification.info(title, description);
    }
  }

  // ===========================================================================
  // Report Task Notifications
  // ===========================================================================

  private showReportTaskNotification(message: ReceiveMessageNotify): void {
    const options: NzNotificationDataOptions = {
      nzKey: message.id,
      nzDuration: 0
    };

    const content = this.buildReportContent(message);
    const status = message.data.status;

    if (status === 2) {
      this.nzNotification.blank(message.title, content, options);
    } else if (status === 3) {
      const ref = this.nzNotification.success(message.title, content, options);
      if (message.data.generatedFileUrl) {
        ref.onClick.subscribe(() => window.open(message.link, '_blank'));
      }
    } else if (status === 4) {
      this.nzNotification.error(message.title, content, options);
    } else {
      this.nzNotification.info(message.title, content, options);
    }
  }

  private buildReportContent(message: ReceiveMessageNotify): string {
    let content = message.description;

    switch (message.data.status) {
      case 1:
        break;
      case 2:
        content = `<nz-icon nzType="sync" [nzSpin]="true" />${message.description}`;
        break;
      case 3:
        if (message.data.generatedFileUrl) {
          content = `${message.description} <br> <a href="${message.data.generatedFileUrl}" target="_blank">Download</a>`;
        }
        break;
    }

    return `${content}<br><small class="text-grey-dark">${new Date().toLocaleString()}</small>`;
  }

  // ===========================================================================
  // Legacy API (Used by existing components)
  // ===========================================================================

  receiveMessage(): Observable<ReceiveMessageNotify> {
    return this.generalMessageSubject.asObservable();
  }

  bankNotifications(): Observable<string> {
    return this.bankNotificationSubject.asObservable();
  }

  getFinancialAdviceObservable(): Observable<string> {
    return this.financialAdviceSubject.asObservable();
  }

  getEInvSubmissionObservable(): Observable<string> {
    return this.einvSubmissionSubject.asObservable();
  }
}
